import {
  CalendarSolidIcon,
  IndianRupeeSignRegularIcon,
  NoteMedicalRegularIcon,
  StethoscopeSolidIcon,
  TagSolidIcon,
  UserDoctorSolidIcon,
  UserNurseSolidIcon,
  VideoSolidIcon,
} from "@elixir/icons";
import {
  BusinessAppointmentConfigs,
  MODES_OF_APT,
  TFilterOption,
  TWorkflowField,
  VIEW_APT_CHANNEL,
  VIEW_APT_STATUS,
  VIEW_SELF_ASSESSMENTS_CHANNEL,
  VIEW_FILTERS,
  VIEW_PAYMENT_CHANNEL,
} from "@elixir/types";
import { Anchor } from "antd";
import { useEffect, useState } from "react";
import FilterListItem from "../../../molecules/chips/workflow-view/filter-list-item";
import { useQueueWorkflowStore } from "@elixir/stores";
import { QUEUE_CONFIG_KEY } from "../../../constants/config";
import { getTreatmentServices } from "../../../utils/bridges/methods";
import useQueueStore from "../../../stores/queue-store";
import { OptionsSelector } from "ui";
import { Filter } from "mongodb";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
const { Link } = Anchor;

const sortFunction = (a: TFilterOption, b: TFilterOption) => {
  return a.display_text?.localeCompare(b.display_text || "") || 0;
};

const staticFilterOptions: { [key: string]: TFilterOption[] } = {
  [VIEW_FILTERS.APPOINTMENT_CHANNEL]: VIEW_APT_CHANNEL.map(
    ({ value, display_text }, index) => ({
      key: index,
      value,
      display_text,
    })
  ).sort(sortFunction),
  [VIEW_FILTERS.APPOINTMENT_MODE]: Object.keys(MODES_OF_APT)
    .map((mode, index) => ({
      key: index,
      value: mode.toLowerCase(),
      display_text: mode,
    }))
    .sort(sortFunction),
  [VIEW_FILTERS.PAYMENT_STATUS]: VIEW_PAYMENT_CHANNEL.map(
    ({ display_text, value }, index) => ({
      key: index,
      value,
      display_text,
    })
  ).sort(sortFunction),
  [VIEW_FILTERS.APPOINTMENT_STATUS]: VIEW_APT_STATUS.map(
    ({ display_text, value }, index) => ({
      key: index,
      value,
      display_text,
    })
  ).sort(sortFunction),
  [VIEW_FILTERS.DOCTOR]: [],
  [VIEW_FILTERS.SERVICES]: [],
  [VIEW_FILTERS.TAGS]: [],
  [VIEW_FILTERS.SELF_ASSESSMENTS]: VIEW_SELF_ASSESSMENTS_CHANNEL.map(
    ({ display_text, value }, index) => ({
      key: index,
      value,
      display_text,
    })
  ).sort(sortFunction),
};

const initialFilterOptions = Object.keys(VIEW_FILTERS).reduce(
  (acc, key) => {
    const options = staticFilterOptions[key];

    acc[key] = {
      options,
      title: key,
    };
    return acc;
  },
  {} as { [key: string]: TWorkflowField }
);

const ApplyFiltersView = () => {
  const clinicId = useQueueStore((state) => state.clinicId);
  const viewData = useQueueWorkflowStore((state) => state.viewData);
  const selectedFiltersOptions = useQueueWorkflowStore(
    (state) => state.selectedFiltersOptions
  );
  const setSelectedFiltersOptions = useQueueWorkflowStore(
    (state) => state.setSelectedFiltersOptions
  );
  const setIsViewEdited = useQueueWorkflowStore(
    (state) => state.setIsViewEdited
  );
  const [selectedFilter, setSelectedFilter] = useState<VIEW_FILTERS>(
    VIEW_FILTERS.TAGS
  );

  const [filtersOptions, setFilterOptions] = useState<{
    [key: string]: TWorkflowField;
  }>(initialFilterOptions);

  const { data: myProfile } = useMyAccessProfileV2();
  useEffect(() => {
    const { tags = [], labels = [] } = JSON.parse(
      localStorage.getItem(QUEUE_CONFIG_KEY) || "{}"
    ) as BusinessAppointmentConfigs;
    const doctors = myProfile?.doctors || [];

    async function setServices() {
      const treatmentServices = await getTreatmentServices({
        clinic_id: clinicId,
      });
      setFilterOptions((prev) => ({
        ...prev,
        [VIEW_FILTERS.SERVICES]: {
          options: treatmentServices
            .filter(({ name }) => !!name)
            .map(({ name, price }, index) => {
              const lowerCaseName = name.toLowerCase();
              return {
                key: index,
                value: `${lowerCaseName}_${price}`,
                display_text: `${lowerCaseName}_[₹${price}]`,
              };
            })
            .sort(sortFunction),
          title: VIEW_FILTERS.SERVICES,
        },
      }));
    }
    setServices();

    setFilterOptions((prev) => ({
      ...prev,
      [VIEW_FILTERS.TAGS]: {
        options: tags
          .map((tag, index) => ({
            key: index,
            value: tag.tag_id,
            color: tag.hex,
            display_text: tag.label,
          }))
          .sort(sortFunction),
        title: VIEW_FILTERS.TAGS,
      },
      [VIEW_FILTERS.LABELS]: {
        options: labels
          .map((tag, index) => ({
            key: index,
            value: tag.label_id,
            color: tag.hex,
            display_text: tag.label,
          }))
          .sort(sortFunction),
        title: VIEW_FILTERS.LABELS,
      },
      [VIEW_FILTERS.DOCTOR]: {
        options: doctors
          .map((doctor, index) => ({
            key: index,
            value: doctor.oid,
            display_text: doctor.name,
          }))
          .sort(sortFunction),
        title: VIEW_FILTERS.DOCTOR,
      },
    }));
  }, [clinicId]);

  useEffect(() => {
    if (viewData) {
      const getFiltersFromFilterExpression = (
        filterExpression?: Filter<TFilterOption>
      ) => {
        const { $and } = filterExpression || {};
        return $and?.map(({ id, options }) => ({
          title: id,
          options: options?.$elemMatch?.$or?.map(
            ({ value }: { value: string | number }) => ({
              value: value,
            })
          ),
        }));
      };

      const { filterExpression } = viewData;
      const filters = getFiltersFromFilterExpression(filterExpression);
      const appliedFilters = (filters || []).reduce(
        (acc, { title, options }) => {
          acc[title] = {
            options,
            title,
          };
          return acc;
        },
        {} as { [key: string]: TWorkflowField }
      );
      setSelectedFiltersOptions(appliedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewData]);

  return (
    <div className="dr-flex dr-flex-row dr-overflow-y-scroll dr-h-full dr-relative">
      <div className="dr-border-r dr-sticky top-0 dr-border-darwin-touch-neutral-200 dr-flex dr-flex-col dr-w-4/10">
        <p className="dr-p-16 HeadlineBold">
          Select the filters for your queue
        </p>
        <Anchor>
          {Object.keys(VIEW_FILTERS).map((filter) => {
            const { title, Icon } =
              filterIdentifierKeyMapping[filter as VIEW_FILTERS];
            const { options } = selectedFiltersOptions[filter] || {};
            return (
              <Link
                href={`#${filter}`}
                title={
                  <FilterListItem
                    Icon={Icon}
                    title={title}
                    isSelected={selectedFilter === filter}
                    onClick={() => setSelectedFilter(filter as VIEW_FILTERS)}
                    count={options?.length}
                  />
                }
                className="p-0"
              />
            );
          })}
        </Anchor>
      </div>
      <div className="dr-flex dr-flex-col dr-w-6/10">
        {Object.keys(VIEW_FILTERS).map((filter) => {
          const { title } = filterIdentifierKeyMapping[filter as VIEW_FILTERS];
          const { options } = filtersOptions[filter] || [];
          return (
            <OptionsSelector
              id={filter}
              onSelect={(option) => {
                setSelectedFilter(filter as VIEW_FILTERS);
                setIsViewEdited(true);
                const clonedFilters = { ...selectedFiltersOptions };
                const filteredOption = clonedFilters[filter]?.options.filter(
                  (clonedOption) =>
                    clonedOption.key === option.key &&
                    clonedOption.value === option.value
                );
                if (filteredOption && filteredOption.length > 0) {
                  clonedFilters[filter] = {
                    ...clonedFilters[filter],
                    options: clonedFilters[filter].options.filter(
                      (clonedOption) =>
                        clonedOption.key !== option.key &&
                        clonedOption.value !== option.value
                    ),
                  };
                } else {
                  clonedFilters[filter] = {
                    ...clonedFilters[filter],
                    title: filter,
                    options: !!clonedFilters[filter]?.options
                      ? [...clonedFilters[filter].options, option]
                      : [option],
                  };
                }
                setSelectedFiltersOptions(clonedFilters);
              }}
              onSelectAll={(allOptions) => {
                setSelectedFilter(filter as VIEW_FILTERS);
                setIsViewEdited(true);
                const clonedFilters = { ...selectedFiltersOptions };
                if (
                  clonedFilters[filter]?.options.length === allOptions.length
                ) {
                  clonedFilters[filter] = {
                    ...clonedFilters[filter],
                    options: [],
                  };
                } else {
                  clonedFilters[filter] = {
                    ...clonedFilters[filter],
                    title: filter,
                    options: allOptions,
                  };
                }

                setSelectedFiltersOptions(clonedFilters);
              }}
              options={options}
              selectedOptions={selectedFiltersOptions[filter]?.options || []}
              title={title}
            />
          );
        })}
      </div>
    </div>
  );
};

const commonStyle = "dr-w-12 dr-h-14";

export const filterIdentifierKeyMapping: Record<
  VIEW_FILTERS,
  { title: string; Icon: JSX.Element }
> = {
  [VIEW_FILTERS.APPOINTMENT_STATUS]: {
    title: "Appointment Status",
    Icon: <CalendarSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.APPOINTMENT_CHANNEL]: {
    title: "Appointment channel",
    Icon: <UserNurseSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.APPOINTMENT_MODE]: {
    title: "Mode of appointment",
    Icon: <VideoSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.DOCTOR]: {
    title: "Main doctor assigned",
    Icon: <UserDoctorSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.SERVICES]: {
    title: "Services",
    Icon: <StethoscopeSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.PAYMENT_STATUS]: {
    title: "Payment status",
    Icon: <IndianRupeeSignRegularIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.TAGS]: {
    title: "Appointment tags",
    Icon: <TagSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.LABELS]: {
    title: "Appointment labels",
    Icon: <TagSolidIcon className={commonStyle} />,
  },
  [VIEW_FILTERS.SELF_ASSESSMENTS]: {
    title: "Self Assessment",
    Icon: <NoteMedicalRegularIcon className={commonStyle} />,
  },
};

export default ApplyFiltersView;
